import { Sparkles } from 'lucide-react';

type TimelineItem = {
  occurred_at?: string | Date | null;
  emotional_fingerprint?: string[];
  title?: string;
};

function toDateLabel(value?: string | Date | null) {
  if (!value) return '—';
  try {
    const d = typeof value === 'string' ? new Date(value) : value as Date;
    return d.toLocaleString(undefined, { month: 'short', year: 'numeric' });
  } catch {
    return String(value);
  }
}

const POSITIVE = new Set(['hopeful','confident','calm','cheerful','optimistic','encouraged','energized','assured','grateful']);
const NEGATIVE = new Set(['anxious','stressed','frustrated','overwhelmed','worried','fearful','uncertain','tired','confused']);

function classify(word: string): 'positive' | 'negative' | 'neutral' {
  const w = word.toLowerCase().trim();
  if (POSITIVE.has(w)) return 'positive';
  if (NEGATIVE.has(w)) return 'negative';
  return 'neutral';
}

function chipTone(kind: 'positive' | 'negative' | 'neutral') {
  if (kind === 'positive') return 'bg-emerald-100 text-emerald-800';
  if (kind === 'negative') return 'bg-rose-100 text-rose-800';
  return 'bg-slate-100 text-slate-800';
}

function deriveInsights(timeline: TimelineItem[]): string[] {
  const items = timeline.filter(t => Array.isArray(t.emotional_fingerprint) && (t.emotional_fingerprint as string[]).length > 0);
  if (items.length < 2) return [];
  const mid = Math.floor(items.length / 2);
  const early = items.slice(0, mid);
  const recent = items.slice(mid);

  const countKinds = (arr: TimelineItem[]) => {
    let pos = 0, neg = 0, neu = 0;
    arr.forEach(it => (it.emotional_fingerprint || []).forEach(w => {
      const k = classify(w);
      if (k === 'positive') pos++; else if (k === 'negative') neg++; else neu++;
    }));
    return { pos, neg, neu };
  };

  const e = countKinds(early);
  const r = countKinds(recent);
  const insights: string[] = [];

  if (e.neg > r.neg && r.pos > e.pos) {
    const recentMonth = toDateLabel(recent[recent.length - 1]?.occurred_at);
    insights.push(`INSIGHT: Early sessions reflected anxious tones, but by ${recentMonth} your language shifted toward calm and hopeful.`);
  }

  const last3 = items.slice(-3);
  const freq: Record<string, number> = {};
  last3.forEach(it => (it.emotional_fingerprint || []).forEach(w => { const k = w.toLowerCase(); freq[k] = (freq[k] || 0) + 1; }));
  const stable = Object.entries(freq).filter(([w, c]) => c >= 2 && classify(w) === 'positive').sort((a,b)=>b[1]-a[1]);
  if (stable.length > 0) {
    insights.push(`INSIGHT: Recent sessions are stabilizing around “${stable[0][0]}”.`);
  }

  if (insights.length === 0) {
    if (r.pos >= e.pos && r.neg <= e.neg) insights.push('INSIGHT: Your overall tone is trending more positive and less anxious.');
  }

  return insights.slice(0, 2);
}

export default function QualitativeSentimentPanel({ timeline }: { timeline: TimelineItem[] }) {
  const cleaned = (timeline || []).map(it => ({
    occurred_at: it.occurred_at,
    emotional_fingerprint: Array.isArray((it as any).emotional_fingerprint) ? (it as any).emotional_fingerprint as string[] : [],
    title: (it as any).title as string | undefined,
  }));

  const insights = deriveInsights(cleaned);
  const hasAny = cleaned.some(t => (t.emotional_fingerprint || []).length > 0);

  return (
    <section className="rounded-3xl border border-slate-200 bg-white/90 p-6 shadow-sm">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-semibold text-slate-900">Qualitative Sentiment</h2>
        <Sparkles className="h-5 w-5 text-royal-blue" />
      </div>
      <p className="mt-2 text-sm text-slate-600">How your words felt over time.</p>

      <div className="mt-4 space-y-3">
        {insights.map((line, idx) => (
          <div key={idx} className="rounded-2xl border border-slate-100 bg-royal-blue/5 px-4 py-3 text-sm text-slate-800">
            <span className="font-semibold text-slate-900">{line}</span>
          </div>
        ))}
        {insights.length === 0 && (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/80 p-6 text-sm text-slate-500">
            Generate a MoM to see longitudinal sentiment shifts.
          </div>
        )}
      </div>

      <div className="mt-5 space-y-3">
        {!hasAny ? (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/80 p-6 text-sm text-slate-500">
            We’ll surface a timeline once MoMs include emotional fingerprints.
          </div>
        ) : (
          cleaned.slice(0, 6).map((it, idx) => (
            <div key={idx} className="flex items-center justify-between gap-3 rounded-2xl border border-slate-100 bg-slate-50/60 px-4 py-3">
              <div>
                <p className="text-sm font-medium text-slate-900">{toDateLabel(it.occurred_at)}</p>
                <p className="text-xs text-slate-500">Top 2 tones captured</p>
              </div>
              <div className="flex items-center gap-2">
                {(it.emotional_fingerprint || []).slice(0,2).map((w, i) => (
                  <span key={i} className={`rounded-full px-3 py-1 text-xs font-semibold ${chipTone(classify(w))}`}>
                    {w}
                  </span>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
}
